import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Table } from 'antd';
import styled from 'styled-components';

import { getShopRequestGroups } from 'apis/ShopRequestAPI';
import { isMysteryShopAdmin, isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import ShopRequestGroupsStyledTable from './ShopRequestGroupsStyledTable';
import ShopRequestGroupTableExpandIcon from './ShopRequestGroupTableExpandIcon';
import ShopRequestGroupTableRow from './ShopRequestGroupTableRow';
import ShopRequestSortIcon from './ShopRequestSortIcon';
import { SHOP_REQUEST_COLUMNS, SHOP_REQUEST_VIEW_GROUP_BY, SHOP_REQUESTS_PAGE_SIZE } from '../constants';
import EditBatchShopRequestsModal from '../EditBatchShopRequestsModal/EditBatchShopRequestsModal';
import ShopRequestGroup from '../GroupTableColumns/ShopRequestGroup';
import ShopRequestGroupStatus from '../GroupTableColumns/ShopRequestGroupStatus';
import { useShopRequestsDataContext } from '../ShopRequestsDataContext';
import { AntdTableExpandIcon, MysteryShopRequestGroup } from '../types';
import {
  getShopRequestGroupKey,
  getShopRequestGroupsWithKeys,
  getShopRequestListingPayload,
  getShopRequestOrderingFromSorter,
  getShopRequestPageFromTableChange,
  getShopRequestSortOrder,
} from '../utils';

const Container = styled.div`
  flex-grow: 1;
  background: var(--background-gray);
  padding: 0 10px;
  border-top: solid 1px var(--line-gray);
`;

const ShopRequestsGroupTable = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const {
    ordering,
    filters,
    selectionMode,
    selectedItemsIds,
    openEditBatchShopRequestsModel,
    setOpenEditBatchShopRequestsModel,
    setSelectedItemsIds,
    setPage,
    setOrdering,
    setItems,
    setTotalItemsIds,
  } = useShopRequestsDataContext();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const enableShopRequestsGroups =
    filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.REQUEST || filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.COMMUNITY;

  const { isLoading, isFetching, data, refetch } = useQuery({
    queryKey: ['shopRequestsGroups', filters, ordering],
    queryFn: () => getShopRequestGroups(getShopRequestListingPayload(filters, ordering)),
    onSuccess: (data) => {
      setItems(data);
      setTotalItemsIds(data.ids);

      // Handle new selection once filters are updated
      // New selection is filtered out previous selection
      const selectionIntersection = selectedItemsIds.filter((id) => data.ids.includes(id));
      setSelectedItemsIds(selectionIntersection);
    },
    refetchOnWindowFocus: false,
    enabled: enableShopRequestsGroups,
  });

  const onSelectAll = (selected: any, selectedRows: any, changeRows: any) => {
    let newSelection: any[] = [];

    if (selected) {
      newSelection = selectedRows
        .flatMap((row: any) => row?.shop_requests.map((shopRequest: any) => shopRequest.id))
        .filter(Boolean);
      setSelectedItemsIds(newSelection);
    } else {
      setSelectedItemsIds(newSelection);
    }

    setSelectedItemsIds(newSelection);
  };

  useEffect(() => {
    const allRowKeyIds = data?.results?.map((group: MysteryShopRequestGroup) => getShopRequestGroupKey(group)) || [];
    setExpandedRowKeys(allRowKeyIds);
  }, [data]);

  const isShopper =
    currentUser && isMysteryShopShopper(currentUser) && !currentUser.isSuperuser && !isMysteryShopAdmin(currentUser);
  const requestGroupColumnWidth = 'calc(28% - 32px)';
  const dueDateColumnWidth = isShopper ? '26%' : '14%';

  const columns: any[] = [
    Table.SELECTION_COLUMN,
    Table.EXPAND_COLUMN,
    {
      id: SHOP_REQUEST_COLUMNS.requestGroup.id,
      key: SHOP_REQUEST_COLUMNS.requestGroup.id,
      title: SHOP_REQUEST_COLUMNS.requestGroup.label,
      width: requestGroupColumnWidth,
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.requestGroup.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => <ShopRequestGroup shopRequestGroup={record} />,
    },
    {
      id: SHOP_REQUEST_COLUMNS.propagation.id,
      key: SHOP_REQUEST_COLUMNS.propagation.id,
      title: SHOP_REQUEST_COLUMNS.propagation.label,
      width: '12%',
      hidden: isShopper,
    },
    {
      id: SHOP_REQUEST_COLUMNS.shopper.id,
      key: SHOP_REQUEST_COLUMNS.shopper.id,
      title: SHOP_REQUEST_COLUMNS.shopper.label,
      width: '16%',
    },
    {
      id: SHOP_REQUEST_COLUMNS.status.id,
      key: SHOP_REQUEST_COLUMNS.status.id,
      title: SHOP_REQUEST_COLUMNS.status.label,
      width: '18%',
      render: (value: any, record: any) => <ShopRequestGroupStatus shopRequestGroup={record} />,
    },
    {
      id: SHOP_REQUEST_COLUMNS.updated_at.id,
      key: SHOP_REQUEST_COLUMNS.updated_at.id,
      title: SHOP_REQUEST_COLUMNS.updated_at.label,
      width: '12%',
    },
    {
      id: SHOP_REQUEST_COLUMNS.dueDate.id,
      key: SHOP_REQUEST_COLUMNS.dueDate.id,
      title: SHOP_REQUEST_COLUMNS.dueDate.label,
      width: dueDateColumnWidth,
    },
  ];

  return (
    <Container>
      <ShopRequestGroupsStyledTable
        columns={columns}
        dataSource={getShopRequestGroupsWithKeys(data?.results)}
        loading={isLoading || isFetching}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: filters.page,
          pageSize: SHOP_REQUESTS_PAGE_SIZE,
          total: data?.count,
          onChange: (pageNumber: number) => {
            setPage(pageNumber);
          },
          showTotal: (total, range) => {
            const displayedItems = range[1] - range[0] + 1;
            return (
              <span>
                Showing {displayedItems} of {total} records
              </span>
            );
          },
        }}
        onChange={(pagination: any, filters: any, sorter: any, extra: any) => {
          if (sorter) {
            const orderBy = getShopRequestOrderingFromSorter(sorter, SHOP_REQUEST_COLUMNS.requestGroup.id);
            if (orderBy !== ordering) {
              setOrdering(orderBy);
            }
          }
          const page = getShopRequestPageFromTableChange(pagination, extra);
          if (page) {
            setPage(page);
          }
        }}
        rowKey="key"
        rowSelection={
          selectionMode
            ? {
                columnWidth: 0, // Hide the selection column in rows
                renderCell: () => null, // Hide checkboxes in each row
                preserveSelectedRowKeys: true,
                onSelectAll,
              }
            : undefined
        }
        expandable={{
          columnWidth: 32,
          expandedRowRender: (shopRequestGroup: any) => (
            <div key={shopRequestGroup.id}>
              {shopRequestGroup.shopRequests.map((request: any) => (
                <ShopRequestGroupTableRow key={request.id} shopRequest={request} refetch={refetch} />
              ))}
            </div>
          ),
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand, record }: AntdTableExpandIcon) => (
            <ShopRequestGroupTableExpandIcon expanded={expanded} onExpand={onExpand} record={record} />
          ),
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record: any) => {
            setExpandedRowKeys((prevKeys) =>
              expanded ? [...prevKeys, record.key] : prevKeys.filter((key) => key !== record.key)
            );
          },
        }}
      />
      {openEditBatchShopRequestsModel && (
        <EditBatchShopRequestsModal refetchData={refetch} onCancel={() => setOpenEditBatchShopRequestsModel(false)} />
      )}
    </Container>
  );
};

export default ShopRequestsGroupTable;
